import { toast } from 'react-toastify';
import { httpPost, httpGet, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import { Constants } from '../Common/Constants';
import { FormatToInputDate } from '../Common/Utilities';


// =============================================
// Rescan Original Invoice (re-queue for scanning)
// =============================================
// NOTE: Backend reuses existing manual-upload + scan pipeline.
// This UI helper only calls the new API.
export const rescanOriginalInvoice = async ({ modelDefId, companyCode, applyToInvoiceDocumentId }) => {
	const requestBody = {
		modelDefId: Number(modelDefId || 0),
		companyCode: Number(companyCode || 0),
		applyToInvoiceDocumentId: Number(applyToInvoiceDocumentId || 0)
	};

	if (!requestBody.applyToInvoiceDocumentId) {
		throw new Error('applyToInvoiceDocumentId is required');
	}

	// Endpoint name is intentionally explicit. Keep aligned with backend.
	return await httpPost('/api/invoicedocument/rescanoriginalinvoice', requestBody);
};



export const needsRevertTo40 = (invoiceDocument, modelDefId, props) => {
	const details = invoiceDocument.invoiceDocumentEditorDetails || [];

	if (modelDefId === 1) {
		return details.some(d => !d.ioNumber || d.isIONumberValid !== "Y");
	}

	if (modelDefId === 2) {
		return details.some(d =>
			!d.level2Key || d.isLevel2KeyValid !== "Y" ||
			!d.level3Key || d.isLevel3KeyValid !== "Y" ||
			!d.resType || d.isCostCodesValid !== "Y"
		);
	}

	return false;
};
export const canLineRevertTo40 = (line) => {
	const status = Number(line.lineStatus);
	if (status > 70 && status !== 100) return false;

	return true;
};
export const isLineInvalid = (line, modelDefId) => {
	if (!line) return false;

	if (modelDefId === 1) {
		return (
			!line.ioNumber ||
			line.isIONumberValid !== 'Y'
		);
	}

	if (modelDefId === 2) {
		return (
			!line.level2Key ||
			line.isLevel2KeyValid !== 'Y' ||

			!line.level3Key ||
			line.isLevel3KeyValid !== 'Y' ||

			!line.resType ||
			line.isCostCodesValid !== 'Y'
		);
	}

	return false;
};



export const creditMemo = async (props, state, close_clickhandler, reopenCallback, setCreditedMemoInvoiceId) => {
	try {
		props.info.inProgress(true);

		const requestBody = {
			invoiceDocumentId: state.invoiceDocument?.invoiceDocumentId,
			invoiceDocumentFileLocation: state.invoiceDocument?.invoiceDocumentFileLocation,
			invoiceDocumentStatusId: state.invoiceDocument?.invoiceDocumentStatusId,
			modelDefId: state.invoiceDocument?.modelDefId
		};

		const response = await httpPost('/api/invoicedocument/creditmemo', requestBody);

		if (response.status === 200) {
			const invoiceDocument = response.data;
			toast.success("Memo has been credited successfully");

			const memoInvoiceDocumentId =
				invoiceDocument?.invoiceDocumentId ||
				invoiceDocument?.invoiceDocument?.invoiceDocumentId ||
				null;

			// Preferred: open nested credit memo editor (no close+reopen)
			if (memoInvoiceDocumentId && typeof setCreditedMemoInvoiceId === 'function') {
				setCreditedMemoInvoiceId(memoInvoiceDocumentId);
			}
			if (memoInvoiceDocumentId && typeof reopenCallback === 'function') {
				reopenCallback(memoInvoiceDocumentId, invoiceDocument);
				return;
			}

			// Fallback: preserve legacy behavior when nested dialog callbacks are not provided
			close_clickhandler(null, true, false, invoiceDocument);
		} else {
			toast.error("Failed to credit the memo. Please try again later.");
		}
	} catch (error) {
		toast.error(<div>Failed to credit the memo.<br />{error?.response?.data}</div>);
	} finally {
		props.info.inProgress(false);
	}
};


export const accept_clickHandler = async (
	state,
	props,
	getErrorMessage,
	handleClientWarning,
	handleScannedVsTotalWarning,
	IsSaveDisabled,
	editorGridRef,
	broadcastSpotLinesRef,
	attachmentRef,
	httpPost,
	close_clickHandler_wrapper,
	validateInvoiceNumberBeforeSubmit = null,
	showConcurrencyConflictDialog = null) => {
	if (props?.info?.modelDefId === 3) {
		broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
	}

	if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
		const canProceed = await validateInvoiceNumberBeforeSubmit();
		if (!canProceed) {
			const validationResponse = getErrorMessage();
			if (validationResponse?.errorList?.length > 0) {
				toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
			}
			return;
		}
	}

	var invoiceDocument = structuredClone(state.invoiceDocument);

	let validationResponse = getErrorMessage();
	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}
	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return;
				}
			}
		}
	}
	const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
	if (!ok) return;

	if (IsSaveDisabled()) {
		validationResponse = getErrorMessage();
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}

	try {
		props.info.inProgress(true);

		invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
		invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
		if (props?.info?.modelDefId === 3) {
			const liveLines = broadcastSpotLinesRef?.current?.lines;
			if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
		}

		const response = await httpPost("/api/invoicedocument/reviewaccept/", invoiceDocument);

		toast.success('Accepted Successfully');

		const updated = response?.data || invoiceDocument;
		const invoiceDocumentId =
			updated?.invoiceDocumentId ||
			updated?.invoiceDocument?.invoiceDocumentId ||
			invoiceDocument?.invoiceDocumentId ||
			null;

		const statusId =
			updated?.invoiceDocumentStatusId ||
			updated?.invoiceDocument?.invoiceDocumentStatusId ||
			invoiceDocument?.invoiceDocumentStatusId ||
			0;

		// Close like Save: bubble wasUpdated + invoice id so list/report refresh works consistently
		if (typeof close_clickHandler_wrapper === 'function') {
			close_clickHandler_wrapper({
				wasUpdated: true,
				statusId,
				isSaved: true,
				updatedRowFields: invoiceDocumentId ? { invoiceDocumentId } : null,
				invoiceDocumentIdOverride: invoiceDocumentId
			});
		}
	} catch (error) {
		if (error?.response?.status === 401 || error?.response?.status === 403) {
			toast.error(<div>Accept Failed<br />{'- user is unauthorize to perform this action'}</div>);
		} else {
			let message = error?.response?.data;
			let concurrencyErrorCode = null;
			const errorCodeMatch = typeof message === 'string'
				? message.match(/^(-?\d+)\|([\s\S]*)$/)
				: null;
			if (errorCodeMatch) {
				concurrencyErrorCode = parseInt(errorCodeMatch[1], 10);
				message = errorCodeMatch[2];
			}

			const isConcurrencyConflict = concurrencyErrorCode === -1
				&& /invoice has been updated by another user|timestamp conflict|concurrency/i.test(message || '');

			if (isConcurrencyConflict && typeof showConcurrencyConflictDialog === 'function') {
				showConcurrencyConflictDialog(message);
			} else {
				toast.error(<div>Accept Failed<br />{error?.response?.data}</div>);
			}
		}
	} finally {
		props.info.inProgress(false);
	}
}

export const reject_clickHandler = async (
	state,
	props,
	getErrorMessage,
	handleClientWarning,
	handleScannedVsTotalWarning,
	IsSaveDisabled,
	editorGridRef,
	broadcastSpotLinesRef,
	attachmentRef,
	httpPost,
	close_clickHandler_wrapper,
	validateInvoiceNumberBeforeSubmit = null,
	showConcurrencyConflictDialog = null) => {
	if (props?.info?.modelDefId === 3) {
		broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
	}

	if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
		const canProceed = await validateInvoiceNumberBeforeSubmit();
		if (!canProceed) {
			const validationResponse = getErrorMessage();
			if (validationResponse?.errorList?.length > 0) {
				toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
			}
			return;
		}
	}

	var invoiceDocument = structuredClone(state.invoiceDocument);
	let validationResponse = getErrorMessage();
	const statusBeforeReject = state.originalInvoiceDocumentStatus;

	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}
	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return;
				}
			}
		}
	}
	const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
	if (!ok) return;

	if (IsSaveDisabled()) {
		validationResponse = getErrorMessage();
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}

	try {
		props.info.inProgress(true);

		invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
		invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
		if (props?.info?.modelDefId === 3) {
			const liveLines = broadcastSpotLinesRef?.current?.lines;
			if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
		}

		const response = await httpPost("/api/invoicedocument/reviewreject/", invoiceDocument);

		toast.success('Rejected Successfully');

		const updated = response?.data || invoiceDocument;
		const invoiceDocumentId =
			updated?.invoiceDocumentId ||
			updated?.invoiceDocument?.invoiceDocumentId ||
			invoiceDocument?.invoiceDocumentId ||
			null;

		const statusId =
			updated?.invoiceDocumentStatusId ||
			updated?.invoiceDocument?.invoiceDocumentStatusId ||
			invoiceDocument?.invoiceDocumentStatusId ||
			0;
		const isInvoiceStatusChanged = statusBeforeReject !== statusId;


		// Close like Save: bubble wasUpdated + invoice id so list/report refresh works consistently
		if (typeof close_clickHandler_wrapper === 'function') {
			close_clickHandler_wrapper({
				wasUpdated: true,
				statusId,
				isSaved: true,
				updatedRowFields: invoiceDocumentId
					? { invoiceDocumentId }
					: null,
				invoiceDocumentIdOverride: invoiceDocumentId
			});
		}
	} catch (error) {
		if (error?.response?.status === 401 || error?.response?.status === 403) {
			toast.error(<div>Reject Failed<br />{'- user is unauthorize to perform this action'}</div>);
		} else {
			let message = error?.response?.data;
			let concurrencyErrorCode = null;
			const errorCodeMatch = typeof message === 'string'
				? message.match(/^(-?\d+)\|([\s\S]*)$/)
				: null;
			if (errorCodeMatch) {
				concurrencyErrorCode = parseInt(errorCodeMatch[1], 10);
				message = errorCodeMatch[2];
			}

			const isConcurrencyConflict = concurrencyErrorCode === -1
				&& /invoice has been updated by another user|timestamp conflict|concurrency/i.test(message || '');

			if (isConcurrencyConflict && typeof showConcurrencyConflictDialog === 'function') {
				showConcurrencyConflictDialog(message);
			} else {
				toast.error('Reject Failed');
			}
		}
	} finally {
		props.info.inProgress(false);
	}
}

// Returns a de-duplicated list of "IO/PO: {ioNumber} | Placement ID: {placementId}" strings
// for placements that the DAO flagged as removed (isDeleted) from the underlying IO/PO,
// so the caller can warn the user before they save an invoice referencing them.
export const getDeletedPlacementErrorLines = (invoiceDocument) => {
	const details = invoiceDocument?.invoiceDocumentEditorDetails || [];
	const lines = [];
	const seen = new Set();

	details.forEach(detail => {
		// The user can remove/unselect individual placements for a detail line via
		// the Placement Level Lookup dialog; those currently-selected placements
		// live in detail.placements. Only flag a placement as an error if it is
		// still actively selected there - once the user unselects it, it should
		// no longer be reported even though the server snapshot (editorPlacements)
		// still marks it as deleted.
		const selectedPlacements = detail?.placements || [];
		if (selectedPlacements.length === 0) return;

		const selectedPlacementIds = new Set(
			selectedPlacements
				.map(p => p?.placementId)
				.filter(id => id !== undefined && id !== null)
		);
		if (selectedPlacementIds.size === 0) return;

		const editorPlacements = detail?.editorPlacements || detail?.EditorPlacements || [];
		editorPlacements.forEach(placement => {
			if (!placement?.isDeleted) return;
			const placementId = placement?.placementId ?? '';
			if (!selectedPlacementIds.has(placementId)) return;

			const ioNumber = placement?.ioNumber ?? '';
			const key = `${ioNumber}||${placementId}`;
			if (seen.has(key)) return;
			seen.add(key);
			lines.push(`IO/PO: ${ioNumber} | Placement ID: ${placementId}`);
		});
	});

	return lines;
};

export const validateInsertionLevelMatching = (invoiceDocument, state, toast) => {
	const details = invoiceDocument.invoiceDocumentEditorDetails || [];
	if (details.length === 0) return true;

	// 1. Determine modes per line. Lines whose selected IO has no placements
	// fall back to "IO level only" behavior for that specific line, and are
	// allowed to coexist with lines that use 'placement' mode — mixing is only
	// disallowed when two lines both have placements available but disagree
	// on the mode explicitly chosen by the user.
	const placementModeLines = details.filter(d => d.ioSessionMode === 'placement');
	const explicitIoModeLinesWithPlacements = details.filter(
		d => d.ioSessionMode === 'io' && Array.isArray(d.placements) && d.placements.length > 0
	);

	if (placementModeLines.length > 0 && explicitIoModeLinesWithPlacements.length > 0) {
		toast.error("Cannot save invoice: All lines must use the same matching mode. Please select either 'Invoice by IO' or 'Invoice by Placement' consistently across all lines.");
		return false;
	}

	const sessionMode = placementModeLines.length > 0 ? 'placement' : (state?.ioSessionMode || state?.invoiceDocument?.ioSessionMode || 'io');

	// 2. Validate Placement allocations sum > 0 (only for lines actually operating in placement mode)
	if (sessionMode === 'placement') {
		let totalAllocation = 0;
		for (const detail of details) {
			const hasPlacements = Array.isArray(detail?.placements) && detail.placements.length > 0;
			if (hasPlacements) {
				totalAllocation += detail.placements.reduce((sum, p) => sum + Number(p?.allocAmount ?? p?.netAmount ?? 0), 0);
			} else {
				// IO-level-only fallback line (no placements available for its IO)
				totalAllocation += Number(detail?.allocatedAmount ?? detail?.ioInvoiceAmount ?? detail?.netAmount ?? 0);
			}
		}

		if (totalAllocation == 0) {
			toast.error("Cannot save invoice: Total placement allocation must be non-zero.");
			return false;
		}

		// 2b. Bug fix: validate each individual placement's allocated amount does not
		// exceed that placement's own remaining budget (amount - consumedAmt), so a
		// user cannot allocate more than the placement/IO actually contains.
		const placementTotals = {};
		for (const detail of details) {
			if (!Array.isArray(detail?.placements)) continue;
			const ioNumber = detail?.ioNumber || '';
			for (const p of detail.placements) {
				if (!p?.placementId) continue;
				const placementKey = `${ioNumber}||${p.placementId}`;
				if (!placementTotals[placementKey]) {
					placementTotals[placementKey] = {
						ioNumber,
						placementId: p.placementId,
						amount: Number(p.amount || 0),
						consumedAmt: Number(p.consumedAmt || 0),
						tolerancePOFlag: detail.tolerancePOFlag,
						tolerancePOAmount: Number(detail.tolerancePOAmount || 0),
						allocated: 0
					};
				}
				placementTotals[placementKey].allocated += Number(p.allocAmount ?? p.netAmount ?? 0);
			}
		}
		for (const totals of Object.values(placementTotals)) {
			const remaining = totals.amount - totals.consumedAmt;
			const placementTolerance = totals.tolerancePOFlag === 'N'
				? totals.tolerancePOAmount
				: (totals.tolerancePOAmount * totals.amount) / 100.00;
			const remainingWithTolerance = Math.max(0, remaining + placementTolerance);
			if (Number(totals.allocated.toFixed(2)) > Number(remainingWithTolerance.toFixed(2))) {
				toast.error(`Cannot save invoice: IO ${totals.ioNumber}, Placement ${totals.placementId} exceeds its remaining budget.`);
				return false;
			}
		}
	}

	// 3. IO Tolerance Validation (applies to BOTH modes since placement aggregate should equal IO limit)
	const allIoNumbersValid = details.every(detail => detail?.ioNumber && detail.isIONumberValid === 'Y');
	if (allIoNumbersValid) {
		const groupedByIO = {};
	for (const detail of details) {
		if (!detail?.ioNumber) continue;
		const key = detail.ioNumber;
		if (!groupedByIO[key]) {
			groupedByIO[key] = {
				ioNumber: detail.ioNumber,
				poAmount: Number(detail.poAmount || 0),
				consumedAmount: Number(detail.consumedAmount || 0),
				tolerancePOFlag: detail.tolerancePOFlag,
				tolerancePOAmount: Number(detail.tolerancePOAmount || 0),
				ioInvoiceAmount: 0
			};
		}

		const hasPlacements = Array.isArray(detail?.placements) && detail.placements.length > 0;
		groupedByIO[key].ioInvoiceAmount += hasPlacements
			? detail.placements.reduce((sum, p) => sum + Number(p?.allocAmount ?? p?.netAmount ?? 0), 0)
			: Number(detail?.allocatedAmount ?? detail?.ioInvoiceAmount ?? 0);
	}

	for (const d of Object.values(groupedByIO)) {
		let remainingAmount = d.poAmount - d.consumedAmount;
		let remainingWithTol = remainingAmount;

		if (d.tolerancePOFlag === 'N') {
			remainingWithTol += d.tolerancePOAmount;
		} else {
			remainingWithTol += (d.tolerancePOAmount * d.poAmount) / 100.00;
		}

		if (remainingWithTol < 0) remainingWithTol = 0;

		const isInvalid = Number(d.ioInvoiceAmount.toFixed(2)) > Number(remainingWithTol.toFixed(2));

		if (isInvalid) {
			toast.error(`Cannot save invoice: IO ${d.ioNumber} exceeds maximum budget (including tolerance).`);
			return false;
		}
		}
	}

	return true;
};



export const save_clickHandler = async (
state,
setState = null,
props,
editorGridRef,
broadcastSpotLinesRef,
attachmentRef,
isTaxDiscrepent,
IsSaveDisabled,
InvoiceDocumentDetailMissingInfo,
allowDiscrepentTax,
getErrorMessage,
handleClientWarning,
handleScannedVsTotalWarning,
isSaveBtnDisabled,
overrideAllChks,
handleOverrideWarning,
httpPost,
httpDelete,
fetchInvoiceDocument,
close_clickHandler,
Constants,
toast,
navigateAfterSave = true,
setSuccessfullSave,
getInvoiceEditorCapabilities,
validateInvoiceNumberBeforeSubmit = null,
	showConcurrencyConflictDialog = null,
	broadCastTaxCode = '') => {
if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
	const canProceed = await validateInvoiceNumberBeforeSubmit();
	if (!canProceed) {
		const validationResponse = getErrorMessage();
		if (validationResponse?.errorList?.length > 0) {
			toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		}
		return false;
	}
}

if (props?.info?.modelDefId === 3) {
	broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
}

	var invoiceDocument = structuredClone(state.invoiceDocument);
	const statusBeforSave = state.originalInvoiceDocumentStatus;
	invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
	// Lines removed by the user that originated from the database. Sent separately so the server
	// deletes only these specific lines instead of deleting and re-inserting all lines.
	invoiceDocument.deletedInvoiceDocumentEditorDetails = (state?.invoiceDocument?.deletedInvoiceDocumentEditorDetails || []);
	// Tax rows removed with deleted DB-originated detail lines. Sent separately so the server
	// deletes only those specific tax rows.
	invoiceDocument.deletedInvoiceDocumentDetailTaxes = (state?.invoiceDocument?.deletedInvoiceDocumentDetailTaxes || []);
	// NOTE: invoiceDocument.invoiceDocumentEditorDetails is kept as the FULL set of current lines
	// through all validation/business-rule processing below (it is relied on by several checks and
	// by the local state update after a successful save). The insert/update/delete payload actually
	// sent to the server is built separately, right before the httpPost call further down, so that
	// deleted/unchanged lines are excluded and timestamps are refreshed only in the outgoing request.
	if (props?.info?.modelDefId === 3) {
		invoiceDocument.serviceTermStartDate = invoiceDocument?.serviceTermStartDateString || '';
		invoiceDocument.serviceTermEndDate = invoiceDocument?.serviceTermEndDateString || '';
	}
	let _isTaxDiscrepent = isTaxDiscrepent(invoiceDocument.invoiceDocumentEditorDetails);
	invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
	if (props?.info?.modelDefId === 3) {
		const liveLines = broadcastSpotLinesRef?.current?.lines;
		if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
	}

	const invalidRows = getInvalidIoPoRowsForServiceTerm(invoiceDocument);

	if (invalidRows.length > 0) {

		toast.error(
			<div>
				{["The following IO/PO(s) have service dates outside the invoice service date range:"]
					.concat(
						invalidRows.map((line, idx) =>
							`- IO/PO ${line.ioNumber} (line #${idx + 1})`
						)
					)
					.map((msg, i) => <div key={i}>{msg}</div>)}
			</div>
		);

		return false;
	}

	if (invoiceDocument?.modelDefId === 1 && invoiceDocument?.insertionLevelMatchingCriteria === 1) {
		if (!validateInsertionLevelMatching(invoiceDocument, state, toast)) {
			return false;
		}
	}

	if (IsSaveDisabled() || InvoiceDocumentDetailMissingInfo() || (!allowDiscrepentTax && _isTaxDiscrepent)) {
		if (state.mode === "ManualEntry" && invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "M") {
			invoiceDocument.invoiceDocumentStatusId = 40;
		} else if (invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "C") {
			invoiceDocument.invoiceDocumentStatusId = 40;
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (isLineInvalid(detail, invoiceDocument.modelDefId) || parseFloat(detail.ioInvoiceAmount || 0) === 0) {
					detail.lineStatus = 40;
				}
			});
		}
	}
	let validationResponse = getErrorMessage();

	// Deleted-placement check is independent of the rest of getErrorMessage()'s
	// validation (so it never changes when other, unrelated errors show/hide).
	// It reuses the same toast call/style, merging its message into whatever
	// errorList already exists, rather than firing a separate toast.
	const deletedPlacementLines = invoiceDocument?.modelDefId === 1
		? getDeletedPlacementErrorLines(invoiceDocument)
		: [];

	if (deletedPlacementLines.length > 0) {
		const combinedErrorList = [
			...(validationResponse?.errorList || []),
			'- The following placement(s) have been removed from the selected IO/PO. Please unselect these line item(s) before proceeding.',
			...deletedPlacementLines.map(line => `  ${line}`)
		];
		toast.error(<div>{combinedErrorList.map((e, i) => <div key={i}>{e}</div>)}</div>, { allowHtml: true });
		return false;
	}

	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return false;
	}

	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return false;
				}
			}
		}
	}

	const chkAllValid =
		state.mode !== "MakeCorrections" ||
		(state.originalInvoiceDocumentStatus !== 60 && state.originalInvoiceDocumentStatus > 41);


	const mustRevertTo40 =
		(state?.invoiceDocument?.invoiceDocumentId || 0) !== 0 &&
		needsRevertTo40(invoiceDocument, props.info.modelDefId, props);

	let _isSaveDisabled;

	if (mustRevertTo40) {
		_isSaveDisabled = false;
	}
	else if (!chkAllValid) {
		_isSaveDisabled = isSaveBtnDisabled;
	}
	else {
		_isSaveDisabled = IsSaveDisabled();
	}

	if (!_isSaveDisabled) {

		if (props.info.modelDefId !== 3) {
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (detail.StatusDescription === "Approved") {
					detail.StatusDescription = "Approved";
				} else if (detail.StatusDescription === "Rejected") {
					detail.StatusDescription = "Rejected";
				}
			});
		}
		const detailLines = invoiceDocument.invoiceDocumentEditorDetails || [];
		const allDetailLinesValid = detailLines.length > 0 && detailLines.every(detail =>
			!isLineInvalid(detail, invoiceDocument.modelDefId) &&
			parseFloat(detail.ioInvoiceAmount || 0) !== 0
		);
		if (invoiceDocument.invoiceDocumentStatusId === 40 && allDetailLinesValid) {
			invoiceDocument.invoiceDocumentStatusId = 50;
			detailLines.forEach(detail => {
				if (Number(detail.lineStatus) < 50) {
					detail.lineStatus = 50;
					detail.isDirty = true;
				}
			});
		}

		if ((invoiceDocument.invoiceDocumentStatusId >= 50 && invoiceDocument.invoiceDocumentStatusAttribute == "C")
			|| state.mode === "ManualEntry" && invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "M"
		) {
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (detail.lineStatus < 50) {
					detail.lineStatus = 50;
				}
			});
		}
		const isApprovalFirstLevel =
			(invoiceDocument.invoiceDocumentStatusId === 50 || invoiceDocument.invoiceDocumentStatusId === 100);

		//if (isApprovalFirstLevel) {
		const mustRevert = needsRevertTo40(invoiceDocument, props.info.modelDefId, props);

		if (mustRevert) {
			invoiceDocument.invoiceDocumentStatusId = 40;

			invoiceDocument.invoiceDocumentEditorDetails.forEach(line => {
				if (
					//canLineRevertTo40(line) &&
					isLineInvalid(line, invoiceDocument.modelDefId)
				) {
					line.lineStatus = 40;
				}
			});

			// Fix: setState called once outside the loop, and originalInvoiceDocumentStatus
			// is kept in sync so the second save correctly identifies the pre-save status.
			setState(prev => ({
				...prev,
				originalInvoiceStatusIdForGadgetLoading: 50,
				originalInvoiceDocumentStatus: 40
			}));
		}
		//}

		if (!_isTaxDiscrepent) {
			invoiceDocument.taxDiscrepancyFlag = 0;
		}

		const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
		if (!ok) return false;

		if (state.mode === Constants.OVERRIDE && overrideAllChks) {
			try {
				await handleOverrideWarning()
			}
			catch {
				return;
			}

			invoiceDocument.isModifiedAfterApproval = 'Y';
			invoiceDocument.flowState = 4;
		}

		const invalidPlacementDetail = (invoiceDocument.invoiceDocumentEditorDetails || []).find(detail =>
			(detail.placements || []).some(placement => {
				const rawPlacementId = placement?.placementId;
				if (rawPlacementId === null || rawPlacementId === undefined || String(rawPlacementId).trim() === '') {
					return true;
				}
				const numericPlacementId = Number(rawPlacementId);
				return Number.isFinite(numericPlacementId) && numericPlacementId <= 0;
			})
		);
		if (invalidPlacementDetail) {
			toast.error('Each selected placement must have a valid placement ID greater than zero.');
			return false;
		}

		props.info.inProgress(true);
		// Build the outgoing payload from the final, fully-validated invoiceDocument without
		// mutating the local grid state. Unlike the old delta approach, the FULL set of active
		// lines (including removed ones, represented as tombstones) is always sent. Each detail,
		// tax, and placement line carries a changeType flag so the server can decide how to
		// persist it: 0 = unchanged, 1 = inserted, 2 = updated, 3 = deleted.
		const CHANGE_TYPE = { UNCHANGED: 0, INSERTED: 1, UPDATED: 2, DELETED: 3 };
		const normalizeTaxAmount = value => {
			const num = Number(value);
			return value == null || isNaN(num) ? 0 : num;
		};
		const normalizeTaxCode = value => (value == null ? '' : String(value).trim());
		const isTaxValuePresent = value => normalizeTaxCode(value) !== '';
		const deletedSeqIds = new Set((invoiceDocument.deletedInvoiceDocumentEditorDetails || []).map(d => d.seqId));
		const taxKey = tax => `${tax.taxCode ?? ''}|${tax.taxTypeCode ?? ''}`;
		const placementKey = placement => String(placement.placementId ?? placement.placementCode ?? '');

		const getTaxTypeForSave = (detailTax, taxList, rowIndex, selectedTaxCode) => {
			const detailTaxCode = detailTax.taxCode ?? detailTax.TaxCode;
			const detailTaxTypeCode = detailTax.taxTypeCode ?? detailTax.TaxTypeCode;
			const taxDefinitions = (taxList || []).filter(taxDefinition =>
				!selectedTaxCode || (taxDefinition.taxCode ?? taxDefinition.TaxCode) === selectedTaxCode
			);
			const definitionsByTaxType = [1, 2]
				.map(taxType => taxDefinitions.find(taxDefinition =>
					Number(taxDefinition.taxType ?? taxDefinition.TaxType) === taxType
				))
				.filter(Boolean);

			const matchingTax = taxDefinitions.find(taxDefinition =>
				(detailTaxTypeCode !== null && detailTaxTypeCode !== undefined) &&
				(detailTaxCode === null || detailTaxCode === undefined ||
					(taxDefinition.taxCode ?? taxDefinition.TaxCode) === detailTaxCode) &&
				(taxDefinition.taxTypeCode ?? taxDefinition.TaxTypeCode) === detailTaxTypeCode
			);

			const tax = matchingTax || (
				(detailTaxCode === null || detailTaxCode === undefined) &&
				(detailTaxTypeCode === null || detailTaxTypeCode === undefined)
					? definitionsByTaxType[rowIndex] || taxDefinitions[rowIndex]
					: detailTaxTypeCode === null || detailTaxTypeCode === undefined
						? taxDefinitions.length === 1 ? taxDefinitions[0] : null
						: null
			);

			return Number(tax?.taxType ?? tax?.TaxType);
		};

		const normalizeTaxIdentifiersForSave = (tax, detail, rowIndex) => {
			const invoiceTaxes = Array.isArray(invoiceDocument?.taxes) ? invoiceDocument.taxes : [];
			const isBroadcastModel = Number(invoiceDocument?.modelDefId) === 3;
			const selectedTaxCode = normalizeTaxCode(detail?.taxCode || (isBroadcastModel ? broadCastTaxCode : ''));
			const hasSelectedTaxCode = isTaxValuePresent(selectedTaxCode);
			const taxType = getTaxTypeForSave(tax, invoiceTaxes, rowIndex, selectedTaxCode);
			const matchedTaxDefinition = invoiceTaxes.find(taxDefinition => {
				const definitionTaxCode = taxDefinition.taxCode ?? taxDefinition.TaxCode;
				const definitionTypeCode = taxDefinition.taxTypeCode ?? taxDefinition.TaxTypeCode;
				const detailTypeCode = tax.taxTypeCode ?? tax.TaxTypeCode;
				if (Number(taxDefinition.taxType ?? taxDefinition.TaxType) !== taxType) return false;
				if (hasSelectedTaxCode && definitionTaxCode !== selectedTaxCode) return false;
				if (detailTypeCode != null && definitionTypeCode !== detailTypeCode) return false;
				return true;
			});

			const resolvedTaxCode = hasSelectedTaxCode
				? selectedTaxCode
				: 'Dummy';
			const resolvedTaxTypeCode = hasSelectedTaxCode
				? (matchedTaxDefinition?.taxTypeCode ?? matchedTaxDefinition?.TaxTypeCode ?? tax.taxTypeCode ?? tax.TaxTypeCode ?? '')
				: (isBroadcastModel && taxType === 2 ? 'Local Tax Dummy' : 'Dummy');

			return {
				...tax,
				taxCode: resolvedTaxCode,
				taxTypeCode: resolvedTaxTypeCode
			};
		};

		const buildTaxesForSave = (detail, originalDetail) => {
			const originalTaxes = (originalDetail?.invoiceDocumentDetailTaxes || [])
				.map((tax, rowIndex) => normalizeTaxIdentifiersForSave(tax, originalDetail, rowIndex));
			const originalTaxMap = new Map(originalTaxes.map(tax => [taxKey(tax), tax]));
			const currentTaxes = (detail.invoiceDocumentDetailTaxes || [])
				.map((tax, rowIndex) => normalizeTaxIdentifiersForSave(tax, detail, rowIndex));
			const currentTaxKeys = new Set(currentTaxes.map(taxKey));

			const taxesForSave = currentTaxes.map(tax => {
				const originalTax = originalTaxMap.get(taxKey(tax));
				let changeType = CHANGE_TYPE.UNCHANGED;
				if (detail.isNew || !originalTax) {
					changeType = CHANGE_TYPE.INSERTED;
				} else {
					const originalAmount = normalizeTaxAmount(originalTax.taxAmount);
					const currentAmount = normalizeTaxAmount(tax.taxAmount);
					const amountChanged = originalAmount !== currentAmount;
					const taxRateChanged = Number(originalTax.taxPrc ?? 0) !== Number(tax.taxPrc ?? 0);

					if (originalAmount === 0 && currentAmount !== 0) {
						changeType = CHANGE_TYPE.INSERTED;
					} else if (amountChanged || taxRateChanged) {
						changeType = CHANGE_TYPE.UPDATED;
					}
				}
				return { ...tax, taxAmount: normalizeTaxAmount(tax.taxAmount), changeType };
			});

			const deletedTaxesForSave = [];
			if (!detail.isNew) {
				originalTaxes.forEach(originalTax => {
					if (!currentTaxKeys.has(taxKey(originalTax))) {
						deletedTaxesForSave.push({ ...originalTax, seqId: detail.seqId, taxAmount: normalizeTaxAmount(originalTax.taxAmount), changeType: CHANGE_TYPE.DELETED });
					}
				});
			}

			return [...deletedTaxesForSave, ...taxesForSave];
		};

		const haveTaxesChanged = (detail, originalDetail) => {
			const currentTaxes = detail.invoiceDocumentDetailTaxes || [];
			const originalTaxes = originalDetail?.invoiceDocumentDetailTaxes || [];

			if (currentTaxes.length !== originalTaxes.length) {
				return true;
			}

			const originalTaxMap = new Map(originalTaxes.map(tax => [taxKey(tax), tax]));
			return currentTaxes.some(tax => {
				const originalTax = originalTaxMap.get(taxKey(tax));
				return !originalTax
					|| Number(originalTax.taxPrc ?? 0) !== Number(tax.taxPrc ?? 0)
					|| Number(originalTax.taxAmount ?? 0) !== Number(tax.taxAmount ?? 0);
			});
		};

		const buildPlacementsForSave = detail => {
			const currentPlacements = (detail.placements || []).map(placement => {
				const changeType = placement.isNew === true
					? CHANGE_TYPE.INSERTED
					: (placement.isDirty === true ? CHANGE_TYPE.UPDATED : CHANGE_TYPE.UNCHANGED);
				return { ...placement, changeType };
			});
			const existingKeys = new Set(currentPlacements.map(placementKey));
			const deletedPlacements = (detail.deletedPlacements || [])
				.filter(placement => !existingKeys.has(placementKey(placement)))
				.map(placement => ({ ...placement, changeType: CHANGE_TYPE.DELETED }));

			return [...currentPlacements, ...deletedPlacements];
		};

		const activeDetailsForSave = (invoiceDocument.invoiceDocumentEditorDetails || [])
			.filter(detail => detail.isNew === true || !deletedSeqIds.has(detail.seqId))
			.map(detail => {
				const originalDetail = state.invoiceDocumentImage?.invoiceDocumentEditorDetails?.find(
					original => original.seqId === detail.seqId
				);
				const taxesChanged = !detail.isNew && haveTaxesChanged(detail, originalDetail);
				const detailForSave = detail.isNew
					? { ...detail, seqId: 0, timestamp: 0, taxAmount: normalizeTaxAmount(detail.taxAmount), changeType: CHANGE_TYPE.INSERTED }
					: {
						...detail,
						taxAmount: normalizeTaxAmount(detail.taxAmount),
						changeType: detail.isDirty || taxesChanged
							? CHANGE_TYPE.UPDATED
							: CHANGE_TYPE.UNCHANGED
					};

				detailForSave.invoiceDocumentDetailTaxes = buildTaxesForSave(detail, originalDetail);
				detailForSave.placements = buildPlacementsForSave(detail);

				return detailForSave;
			});

		const deletedDetailsForSave = (invoiceDocument.deletedInvoiceDocumentEditorDetails || []).map(detail => ({
			...detail,
			taxAmount: normalizeTaxAmount(detail.taxAmount),
			changeType: CHANGE_TYPE.DELETED,
			invoiceDocumentDetailTaxes: (detail.invoiceDocumentDetailTaxes || []).map(tax => ({ ...tax, seqId: detail.seqId, taxAmount: normalizeTaxAmount(tax.taxAmount), changeType: CHANGE_TYPE.DELETED })),
			placements: (detail.deletedPlacements || detail.placements || []).map(placement => ({ ...placement, changeType: CHANGE_TYPE.DELETED }))
		}));

		const invoiceDocumentForSave = {
			...invoiceDocument,
			invoiceDocumentEditorDetails: [...activeDetailsForSave, ...deletedDetailsForSave]
		};
		delete invoiceDocumentForSave.deletedInvoiceDocumentEditorDetails;
		delete invoiceDocumentForSave.deletedInvoiceDocumentDetailTaxes;

		try {
			const response = await httpPost(
				"/api/invoicedocument/invoicedocumenteditor/",
				invoiceDocumentForSave
			);

			if (typeof response?.data?.records === 'string') {
				let message = response?.data?.records;
				if (message.indexOf('error') === 0) {
					message = message.replace('error: ', '');

					// The upsert path (apas_invoice_document_detail_set) prefixes its thrown message
					// with "<errorCode>|" (e.g. "-1|Invoice has been updated by another user...") so
					// we can key off the exact SP error_code rather than matching on message text.
					let concurrencyErrorCode = null;
					const errorCodeMatch = message.match(/^(-?\d+)\|([\s\S]*)$/);
					if (errorCodeMatch) {
						concurrencyErrorCode = parseInt(errorCodeMatch[1], 10);
						message = errorCodeMatch[2];
					}

					// Error code -1 is also used by other business validations. Only show the
					// Reload/Cancel dialog when the error is the timestamp conflict response.
					const isConcurrencyConflict = concurrencyErrorCode === -1
						&& /invoice has been updated by another user|timestamp conflict|concurrency/i.test(message);

					if (isConcurrencyConflict) {
						if (typeof showConcurrencyConflictDialog === 'function') {
							showConcurrencyConflictDialog(message);
						} else {
							toast.error(<div>{message}</div>);
						}
						props.info.inProgress(false);
						return false;
					}

					const partialKeywords = ['start date', 'end date', 'run date', 'calendar'];

					const containsKeyword = partialKeywords.some(keyword =>
						message.toLowerCase().includes(keyword)
					);

					if (containsKeyword) {
						toast.error(<div>{message}</div>);
					} else if (message.indexOf('Invoice acceptance reverted') === 0) {
						toast.error(<div>{message}</div>);
					} else {
						// Fix: show the actual server message so the user sees a meaningful error
						// instead of a blank toast. Fall back to a generic label only when empty.
						const displayMessage = message?.trim() || 'An unexpected error occurred. Please try again.';
						toast.error(<div>Update Failed<br /><span style={{ whiteSpace: 'pre-line' }}>{displayMessage}</span></div>);
					}
					props.info.inProgress(false);
					return false;

				}

			} else {

				// Fire delete for broadcast spot lines that were removed before saving
				if (props.info.modelDefId === 3) {
					const deletedIds = broadcastSpotLinesRef?.current?.deletedSequenceIds || [];
					if (deletedIds.length > 0) {
						const invoiceDocumentId = invoiceDocument?.invoiceDocumentId;
						const sequenceIds = deletedIds.join(',');
						try {
							await httpDelete(
								`/api/invoicedocument/broadcastspotlines?sequenceIds=${encodeURIComponent(sequenceIds)}&invoiceDocumentId=${invoiceDocumentId}`
							);
							const refreshedInvoice = await fetchInvoiceDocument();
							if (refreshedInvoice) response.data.records = refreshedInvoice;
							broadcastSpotLinesRef?.current?.clearDeletedSequenceIds?.();
						} catch (e) {
							console.error('Failed to delete broadcast spot lines:', e);
						}
					}
				}

				toast.success('Updated Successfully');
				setSuccessfullSave(true);
				if (navigateAfterSave) {

					const updatedInvoice = response.data?.records;
					let savedInvoiceDocumentId = updatedInvoice?.invoiceDocumentId;

					// For NEW invoices, the backend may not immediately return an id (or it may take a moment
					// before it becomes queryable). Try to resolve it by file location with retries.
					if (!savedInvoiceDocumentId) {
						const fileKey = invoiceDocument?.invoiceDocumentFileLocation;
						const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
						if (fileKey) {
							const attempts = 3;
							const delayMs = 2000;
							for (let i = 0; i < attempts; i++) {
								try {
									const byFileRes = await httpGet(
										`/api/invoicedocument/invoicedocumenteditorbyfilename?invoiceDocumentFileLocation=${encodeURIComponent(fileKey)}`
									);
									savedInvoiceDocumentId = byFileRes?.data?.invoiceDocumentId;
									if (savedInvoiceDocumentId) break;
								} catch (e) {
									// swallow and retry
								}
								if (i < attempts - 1) {
									await delay(delayMs);
								}
							}
						}
					}

					if (savedInvoiceDocumentId && updatedInvoice) {
						updatedInvoice.invoiceDocumentId = savedInvoiceDocumentId;
					}
					const hasAuthority = updatedInvoice?.invoiceDocumentEditorDetails?.some(e => e.hasAuthority === "Y") || false;
					const statusId = updatedInvoice?.invoiceDocumentStatusId;
					const approvalLevels = updatedInvoice?.companyApprovalConfiguration?.routingNumberOfApprovalLevels || 0;
					const approvedStatus = (approvalLevels * 20) + 50;
					const wasSavedSuccessfully = true;
					const isInvoiceStatusChanged = (statusBeforSave) != statusId;

					const shouldKeepOpen =
						statusId >= 40 &&
						statusId < 70 &&
						statusId !== 60 &&
						((statusId === 40 && Number(statusBeforSave) > 40) ||
							(statusId !== 40 && hasAuthority && isInvoiceStatusChanged));


					const totalAmount = updatedInvoice?.invoiceDocumentEditorDetails?.reduce((sum, line) => {
						const net = Number(line?.ioInvoiceAmount || 0);
						const tax = Number(line?.taxAmount || 0);
						return sum + net + tax;
					}, 0);

					const vendor = updatedInvoice.modelDefId === 3
						? `${updatedInvoice.callLetters || ''}` +
						`${updatedInvoice.callLetters && updatedInvoice.band ? '-' : ''}` +
						`${updatedInvoice.band || ''}` +
						`${(updatedInvoice.callLetters || updatedInvoice.band) && updatedInvoice.vendor ? ': ' : ''}` +
						`${updatedInvoice.vendor || ''}`
						: updatedInvoice.vendor;

					const updatedRowFields = {
						hasAuthority: hasAuthority,
						invoiceDocumentId: savedInvoiceDocumentId || updatedInvoice.invoiceDocumentId,
						invoiceDocumentStatusId: statusId === 40 ? 50 : statusId,
						status: approvedStatus === statusId ? "Ready to Post" : updatedInvoice.status,
						invoiceNumber: updatedInvoice.invoiceNumber,
						totalAmount: totalAmount.toFixed(2),
						dueDate: updatedInvoice.dueDate,
						...(updatedInvoice.modelDefId === 3
							? { payTo: vendor }
							: { vendor })
					};
					const newCaps = getInvoiceEditorCapabilities(updatedInvoice);

					setState(prev => ({
						...prev,
						invoiceDocument: {
							...prev.invoiceDocument,
							...updatedInvoice,
							invoiceDocumentStatusId: statusId === 40 ? 50 : statusId,
							deletedInvoiceDocumentEditorDetails: [],
							deletedInvoiceDocumentDetailTaxes: []
						},
						invoiceDocumentImage: structuredClone({
							...prev.invoiceDocument,
							...updatedInvoice,
							invoiceDocumentStatusId: statusId,
							deletedInvoiceDocumentEditorDetails: [],
							deletedInvoiceDocumentDetailTaxes: []
						}),
						capabilities: newCaps.capabilities,
						originalInvoiceDocumentStatus: statusId,
						originalInvoiceStatusIdForGadgetLoading: statusId,
						mode: statusId === 40 ? Constants.MAKE_CORRECTIONS : prev.mode,
						isDirty: false,
						updatedRowFields: updatedRowFields
					}));

					// If we couldn't resolve an invoiceDocumentId for a create, do NOT close.
					if (!updatedRowFields.invoiceDocumentId) {
						toast.warning('Saved successfully, but invoice id was not returned yet. Please wait a moment and click Save again.');
						return true;
					}

					if (!shouldKeepOpen) {
						close_clickHandler(
							null,
							false,
							true,
							null,
							statusId,
							wasSavedSuccessfully,
							updatedRowFields,
							false,
							null,
							updatedRowFields.invoiceDocumentId
						);
					}
				}

			}

			props.info.inProgress(false);
			return true;

		}

		catch (error) {
			props.info.inProgress(false);
			//Invoice acceptance reverted due to no resource available for approval of next level.
			toast.error(<div>Update Failed<br />{error?.response?.data?.detail}</div>);
			return false;

		}
	}
	else {
		if (validationResponse.errorList.length == 0 && !invoiceDocument?.invoiceDocumentFileLocation) {
			validationResponse.errorList = ['The invoice encountered the following errors', '- Invoice document is missing'];
		}
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return false;
	}

}

export const savePostedInvoiceComments = async (
	state,
	props,
	httpPost,
	toast,
	setSuccessfullSave,
	close_clickHandler
) => {
	const invoiceDocument = state?.invoiceDocument;

	if (!invoiceDocument?.invoiceDocumentId) {
		toast.error("Invalid invoice document");
		return false;
	}

	try {
		props.info.inProgress(true);

		await httpPost(
			"/api/invoicedocument/savepostedcomments",
			invoiceDocument
		);

		toast.success("Comments updated successfully");
		setSuccessfullSave(true);

		// 🔥 ONLY reload gadget 4 row
		close_clickHandler(
			null,
			false,
			true,
			{ reloadMode: "POSTED_COMMENT_ONLY" },
			invoiceDocument.invoiceDocumentStatusId,
			true,
			null,
			false,
			null,
			invoiceDocument.invoiceDocumentId
		);

		return true;

	} catch (error) {
		console.error("Save posted comments error:", error);
		toast.error("Failed to update comments");
		return false;
	} finally {
		props.info.inProgress(false);
	}
};


export const routeToEmployee_clickHandler = async (
	state,
	setState,
	handleRouteToEmployeeWarning,
	save_clickHandler) => {
	let isSaveConfirmed = false;

	if (state.isDirty) {
		try {
			isSaveConfirmed = await handleRouteToEmployeeWarning();
			if (isSaveConfirmed) {
				const saveSuccessful = await save_clickHandler();
				if (!saveSuccessful) {
					return;
				}
			}
		} catch (error) {
			return;
		}
	}

	const isProduction = state.invoiceDocument?.modelDefId === 2;
	setState((prev) => ({
		...prev,
		openPOHeaderResourceDialog: !isProduction,
		openLevel2ResourceDialog: isProduction,
		isDirty: isSaveConfirmed ? false : prev.isDirty,
		routeToEmployeeDialogAction: null,
		routeToEmployeeDialogCurrentStatusId: 0
	}));
};

export const confirmScannedVsTotal = async ({ invoiceDocument = null, state = null }, handleScannedVsTotalWarning, props, tolerance = 0.01) => {
	const scannedTotal = Number(invoiceDocument?.scannedTotal ?? 0);
	const { mode } = state;
	const details = invoiceDocument?.invoiceDocumentEditorDetails || [];
	const calculatedTotal = details.reduce(
		(sum, d) =>
			sum +
			Number(d?.ioInvoiceAmount ?? d?.invoiceAmount ?? 0) +
			Number(d?.taxAmount ?? 0),
		0
	);

	if (!Number.isNaN(scannedTotal)) {
		const diff = Math.abs(scannedTotal - calculatedTotal);
		if (diff < tolerance) {
			invoiceDocument.userAcceptDiscrepancy = '';
			return true;
		}
	}

	if (invoiceDocument?.userAcceptDiscrepancy && invoiceDocument.userAcceptDiscrepancy.trim() !== '') {
		return true;
	}
	if (mode === 'UserEntry' || invoiceDocument?.invoiceDocumentFileLocation?.toUpperCase()?.includes('USER_ENTRY_') || mode == Constants.CREDIT_MEMO) {
		return true;
	}

	if (!Number.isNaN(scannedTotal)) {
		const diff = Math.abs(scannedTotal - calculatedTotal);
		if (diff >= tolerance) {
			// Format both totals with commas and two decimal places
			const formattedScanned = scannedTotal.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			const formattedCalculated = calculatedTotal.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});

			const msg = `The scanned total amount (${formattedScanned}) doesn't match the invoice total amount (${formattedCalculated}). Do you want to continue?`;
			try {
				await handleScannedVsTotalWarning(msg);
				const resourceId = (JSON.parse(localStorage.getItem('userInfo') || '{}')?.id ?? 'System');
				invoiceDocument.userAcceptDiscrepancy =
					(invoiceDocument?.userAcceptDiscrepancy ?? '').toString().trim() !== ''
						? invoiceDocument.userAcceptDiscrepancy
						: resourceId;
			} catch {
				return false;
			}
		}
	}
	return true;
};



export const handleRouteToEmployeeWarning = (
	setOpenRouteToEmployeeDialog,
	setDialogResponseHandler) => {


	return new Promise((resolve) => {
		setOpenRouteToEmployeeDialog(true);

		const handleResponse = (event) => {
			setOpenRouteToEmployeeDialog(false);
			if (event.response === 'Y') {
				resolve(true);
			} else {
				resolve(false);
			}

		};
		setDialogResponseHandler(() => handleResponse);

	})
}

export const handleClientWarning = (setOpenClientWarningDialog,
	setDialogResponseHandler) => {
	return new Promise((resolve, reject) => {
		setOpenClientWarningDialog(true);

		const handleResponse = (event) => {
			setOpenClientWarningDialog(false);
			if (event.response === 'Y') {
				resolve();
			} else {
				reject();
			}
		};

		setDialogResponseHandler(() => handleResponse);
	});
};
export const handleOverrideWarning = (
	setConfirmationDialogOpen,
	setOverrideDialogResponseHandler
) => {
	return new Promise((resolve, reject) => {
		setConfirmationDialogOpen(true);

		const handleResponse = (event) => {
			setConfirmationDialogOpen(false);
			if (event.response === 'Y') {
				resolve();
			} else {
				reject();
			}
		};

		setOverrideDialogResponseHandler(() => handleResponse);
	});
};

export const handleCustomWarning = (
	setDialogOpen,
	setDialogResponseHandler,
	setDialogMessage,
	message
) => {
	return new Promise((resolve, reject) => {
		setDialogMessage(message);
		setDialogOpen(true);

		const handleResponse = (event) => {
			setDialogOpen(false);
			if (event.response === 'Y') resolve();
			else reject();
		};

		setDialogResponseHandler(() => handleResponse);
	});
};


//export const routingHistory_clickHandler = (setState, invoiceDocumentId, seqId) => {
//	setState((prev) => ({
//		...prev,
//		openInvoiceDocumentRoutingHistoryDialog: true,
//		routingHistoryData: { invoiceDocumentId, seqId, historyTitle: "Line Level Approval History" }
//	}));
//}
export const invoiceDocumentRoutingHistoryDialog_closeHandler = (setState) => {
	setState((prev) => ({ ...prev, openInvoiceDocumentRoutingHistoryDialog: false }));
}

export const input_changeHandler = (event, state, setState, setDateValidations) => {
	var invoiceDocument = state.invoiceDocument;
	var targetName = event.currentTarget.name;
	invoiceDocument[event.currentTarget.name] = event.currentTarget.value;
	if (event.currentTarget.dataset && event.currentTarget.dataset.name) {
		invoiceDocument[event.currentTarget.dataset.name] = event.currentTarget.value;
	}

	//Reset if currency not matching
	if (targetName == "currencyCode" && event.currentTarget.value != invoiceDocument.currencyCode) {
		invoiceDocument.vendorCode = "";
		invoiceDocument.shortName = "";
		invoiceDocument.vendor = "";
		invoiceDocument.siteId = "";
		//invoiceDocument.currencyCode = "";
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: false }));
	}
	else if (targetName === 'invoiceDateString' || targetName === 'invoiceDateStringTop') {
		if (targetName === 'invoiceDateStringTop') {
			invoiceDocument.invoiceDateString = event.currentTarget.value;
		}
		let nglDate = state.invoiceDocument?.glDateString;
		if (!nglDate) {
			nglDate = event.currentTarget.value;
		}
		state.invoiceDocument.glDate = nglDate;
		state.invoiceDocument.glDateString = nglDate;
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: true }));
	}
	else
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: true }));
	setDateValidations(invoiceDocument);
}

export const onPaymentTermsChange = (
	event,
	state,
	setState,
	setDateValidations,
	input_changeHandler,
	addDaysInDate) => {
	input_changeHandler(event, state, setState, setDateValidations);

	var invoiceDocument = state.invoiceDocument;
	if (event.currentTarget.name === 'invoiceDateStringTop') {
		invoiceDocument.invoiceDateString = event.currentTarget.value;
	}

	var paymentTermCode = event.currentTarget.name === 'paymentTermCode'
		? event.currentTarget.value
		: invoiceDocument?.paymentTermCode;
	var paymentTerm = invoiceDocument?.paymentTerms?.find(val => val.termsCode === paymentTermCode);

	if (!paymentTerm || !invoiceDocument?.invoiceDateString) {
		invoiceDocument.dueDate = null;
		invoiceDocument.dueDateString = '';
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument }));
		setDateValidations(invoiceDocument);
		return;
	}

	var daysDue = parseInt(paymentTerm.daysDue, 10);
	var invoiceDate = new Date(invoiceDocument.invoiceDateString);
	var dueDate = addDaysInDate(invoiceDate, daysDue);
	var isValidDueDate = ((dueDate).toString()).indexOf('Invalid Date') === -1;

	if (dueDate && isValidDueDate && dueDate?.toISOString().length > 10) {
		invoiceDocument.dueDate = dueDate;
		invoiceDocument.dueDateString = dueDate.toISOString().slice(0, 10);
	}
	else {
		invoiceDocument.dueDate = null;
		invoiceDocument.dueDateString = '';
	}

	setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument }));
	setDateValidations(invoiceDocument);
}

export const serviceTerm_blurHandler = (
	event,
	props,
	state,
	setState,
	currentEditRow,
	toast,
	stringIsNullOrEmpty,
	uniqueBy,
	editorGridRef) => {
	if (props.info.modelDefId !== 3) return;
	var pocodes = [];
	if (state.invoiceDocument?.invoiceDocumentEditorDetails.length > 0) {
		var invoiceDocument = state.invoiceDocument;
		var serviceTermStartDate = invoiceDocument.serviceTermStartDate;
		var serviceTermEndDate = invoiceDocument.serviceTermEndDate;
		if (stringIsNullOrEmpty(state.invoiceDocument?.serviceTermStartDate) || stringIsNullOrEmpty(state.invoiceDocument?.serviceTermEndDate)) {
			state.invoiceDocument?.invoiceDocumentEditorDetails.map(function (detail, index) {
				detail.deliveryAmount = 0.00;
			});
			if (currentEditRow) {
				currentEditRow.deliveryAmount = 0.00;
			}
		}
		else if (state.serviceTermStartDate !== state.invoiceDocument?.serviceTermStartDate || state.serviceTermEndDate !== state.invoiceDocument?.serviceTermEndDate) {
			if ((state.serviceTermStartDate?.length || 0) > 10) {
				return;
			}
			var uniqueInvoiceDocumentEditorDetails = uniqueBy(state.invoiceDocument?.invoiceDocumentEditorDetails, ["ioNumber"]);
			uniqueInvoiceDocumentEditorDetails.map(function (detail, index) {
				if (!stringIsNullOrEmpty(detail.ioNumber)) pocodes.push(detail.ioNumber.trim());
			});
			var params = {
				pocodes: pocodes,
				serviceTermStartDate: stringIsNullOrEmpty(state.invoiceDocument?.serviceTermStartDate) ? null : state.invoiceDocument?.serviceTermStartDate,
				serviceTermEndDate: stringIsNullOrEmpty(state.invoiceDocument?.serviceTermEndDate) ? null : state.invoiceDocument?.serviceTermEndDate
			}
			props.info.inProgress(true);
			httpPost("/api/lookup/poheaderlistbypocodelist/", params)
				.then((response) => {
					var poHeaders = response.data;
					editorGridRef.current.setDetailsOnserviceTermBlur(poHeaders)
					if (currentEditRow) {
						currentEditRow.deliveryAmount = poHeaders.find(poHeader => poHeader.poCode.trim() === currentEditRow.ioNumber.trim()).deliveryAmount;
					}
					props.info.inProgress(false);
				}).catch((error) => {
					props.info.inProgress(false);
					if (error.response && error.response.data) {
						let errorsList = [];
						let errors = error.response.data?.errors;
						if (errors.response.data?.title) {
							errorsList = [errors.response.data?.title]
						}
						toast.error(<div>Delivery Amount update failed<br />{errorsList.map((e, i) => (<div>{e}</div>))}</div>);
					}
				});
		}
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, serviceTermStartDate: serviceTermStartDate, serviceTermEndDate: serviceTermEndDate }));
	}
}
export const vendorDialog_closeHandler = (
	event,
	vendor,
	state,
	setState,
	stringIsNullOrEmpty,
	editorGridRef,
	setVendor) => {
	if (vendor && !stringIsNullOrEmpty(state.invoiceDocument?.vendorCode) && state.invoiceDocument?.vendorCode !== vendor.vendorCode &&
		((state.invoiceDocument?.modelDefId === 1 && state?.invoiceDocument?.invoiceDocumentEditorDetails.length > 0) ||
			(state.invoiceDocument?.modelDefId === 2 && !stringIsNullOrEmpty(state.invoiceDocument?.poCode)))
	) {
		setState((prev) => ({ ...prev, openVendorDialog: false, vendor: vendor, openChangeVendorConfirmation: true }));
	}
	else {
		setState((prev) => ({ ...prev, openVendorDialog: false, poHeaderLookup: [], isDirty: true }));
		setVendor(vendor);
	}
}
export const clientDialog_closeHandler = (
	event,
	selectedClient,
	setState) => {
	const newClientCode = selectedClient ? selectedClient.clientCode : '';
	const newClientName = selectedClient ? selectedClient.clientName : '';
	setState((prev) => {
		return {
			...prev,
			openClientDialog: false,
			isDirty: true,
			invoiceDocument: {
				...prev.invoiceDocument,
				clientCode: newClientCode,
				clientName: newClientName,
				client: newClientCode && newClientName ? `${newClientCode} - ${newClientName}` : '',
			},
			previousClientData: selectedClient || null,
			poHeaderLookup: []
		};
	});
};
export const search_clickHandler = (event, setState) => {
	setState((prev) => ({ ...prev, openVendorDialog: true }));
}
export const searchClient_clickHandler = (event, setState) => {
	setState((prev) => ({ ...prev, openClientDialog: true, fetchClientData: true }));
}
export const handleTabInvoiceDetails = (setActiveTab) => {
	setActiveTab("tabInvoiceDetails");
};

export const handleTabAttachments = (setActiveTab) => {
	setActiveTab("tabAttachments");
};


export const VIEW_ATTRIBUTE = {
	READ_ONLY: 'READ_ONLY',
	EDITABLE: 'EDITABLE',
	APPROVAL: 'APPROVAL'
};

export const STATUS_ATTRIBUTE = {
	NORMAL: 'NORMAL',
	CORRECTION: 'CORRECTION',
	MANUAL: 'MANUAL'
};




export const INVOICE_EDITOR_TEST_CASES = [

	// 1️⃣ Simple View-only invoice
	{
		name: 'View only – no edit rights',
		invoiceDocument: {
			invoiceDocumentId: 1,
			invoiceDocumentStatusId: 40,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'View Invoice'
		}
	},

	// 2️⃣ Editable invoice (opened from list)
	{
		name: 'Editable invoice – normal status',
		invoiceDocument: {
			invoiceDocumentId: 2,
			invoiceDocumentStatusId: 40,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Invoice'
		}
	},

	// 3️⃣ Make Corrections case
	{
		name: 'Correction mode invoice',
		invoiceDocument: {
			invoiceDocumentId: 3,
			invoiceDocumentStatusId: 50,
			invoiceDocumentStatusAttribute: 'C',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'CORRECTION',
			mode: 'MakeCorrections',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Make Corrections'
		}
	},

	// 4️⃣ Manual Entry invoice
	{
		name: 'Manual Entry invoice',
		invoiceDocument: {
			invoiceDocumentId: 4,
			invoiceDocumentStatusId: 50,
			invoiceDocumentStatusAttribute: 'M',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'MANUAL',
			mode: 'ManualEntry',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Invoice'
		}
	},

	// 5️⃣ Approval – user has authority
	{
		name: 'Approval stage – approver',
		invoiceDocument: {
			invoiceDocumentId: 5,
			invoiceDocumentStatusId: 70,
			invoiceDocumentStatusAttribute: 'C',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'APPROVAL',
			uiStatusAttribute: 'CORRECTION',
			mode: 'Approval',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'Approve Invoice'
		}
	},

	// 6️⃣ Ready to Post
	{
		name: 'Ready to Post',
		invoiceDocument: {
			invoiceDocumentId: 6,
			invoiceDocumentStatusId: 170,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N',
			approvalLinkText: 'Post to ERP System'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: true,
			showRepost: false,
			title: 'View Invoice'
		}
	},

	// 7️⃣ Posted – Repost allowed
	{
		name: 'Posted – Repost allowed',
		invoiceDocument: {
			invoiceDocumentId: 7,
			invoiceDocumentStatusId: 180,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N',
			approvalLinkText: 'Repost to ERP System'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: true,
			title: 'View Invoice'
		}
	},

	// 8️⃣ Archived invoice
	{
		name: 'Archived invoice',
		invoiceDocument: {
			invoiceDocumentId: 8,
			invoiceDocumentStatusId: 999,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'View Invoice'
		}
	}
];




//export const isFieldReadOnly = (fieldName, props, invoiceIndicator, isEditable) => {

//	const isCreditMemoMode = props.info.mode == Constants.CREDIT_MEMO;
//	const enableForCreditMemo = isCreditMemoMode && invoiceIndicator === 2;
//	const systemCheck = () => {
//		switch (fieldName) {
//			case "Invoice Number":
//				return (
//					(!isCreditMemoMode && (state.readOnly || isInvoiceSource || !broadCastValidation.isInvoiceNumberEnabled)) || !enableForCreditMemo
//				);
//				break;
//			case "Invoice Date":

//				return (
//					(!isCreditMemoMode && ((!ignorePreviousCases && (state.readOnly || isInvoiceSource || !broadCastValidation.isInvoiceDateEnabled)) || (readOnlyForAdmin))) || !enableForCreditMemo
//				);

//			case "GL Date":

//				return (
//					(!isCreditMemoMode && ((props.info.mode !== Constants.EDIT_DATES && (state.readOnly || isInvoiceSource || !broadCastValidation.isGLDateEnabled))
//						|| (readOnlyForAdmin))) || !enableForCreditMemo);

//			case "Due Date":
//				return (


//				);
//			default:
//				return false; // No system restrictions by default
//		}
//	};

//	// If system check disables field, stop here
//	if (systemCheck()) {
//		return true;
//	}

//	// Else, apply group rule check
//	return !isEditable(fieldName);
//};



// -----------------------------
// UI-side editor truth engine
// -----------------------------
// Backend returns only concrete facts:
//  - isAdmin (server-truth)
//  - isDifferentCompany (logged-in company != invoice company)
//  - routingNumberOfApprovalLevels (max approval depth)
// Everything else (mode + abilities) is derived on the UI.

export const isMultipleOf20 = (n) => (Number(n) % 20 === 0);

// Backend stores auto-approved as "Y"/"N" (most common), but some DTOs may expose
// it as boolean/number depending on the source (list/report vs editor).
export const getIsAutoApproved = (invoiceDocument) => {
	const v =
		invoiceDocument?.isAutoApproved ??
		invoiceDocument?.IsAutoApproved ??
		invoiceDocument?.is_auto_approved ??
		invoiceDocument?.autoApproved ??
		invoiceDocument?.auto_approved;

	if (v === true) return true;
	if (v === 1 || v === "1") return true;
	if (typeof v === "string") {
		const s = v.trim().toUpperCase();
		return s === "Y" || s === "TRUE";
	}
	return false;
};

export const resolveWorkflowMode = (invoiceDocument, routingNumberOfApprovalLevels = 0, isDifferentCompany = false) => {
	if (!invoiceDocument) return Constants.VIEW;

	// Company mismatch is represented as VIEW on UI (with a separate flag to show change-company UI).
	if (isDifferentCompany) return Constants.VIEW;

	// Business rule: ModelDef 3 invoices coming from source (invoiceSource === 1) are always view-only.
	if (Number(invoiceDocument?.modelDefId) === 3 && Number(invoiceDocument?.invoiceSource) === 1) {
		return Constants.VIEW;
	}

	// Structural
	if ((invoiceDocument.applyToInvoiceDocumentId || 0) > 0) return Constants.CREDIT_MEMO;

	// User entry
	if (invoiceDocument.invoiceDocumentStatusAttribute === "U") return Constants.USER_ENTRY;

	// Status-driven workflow
	const statusId = Number(invoiceDocument.invoiceDocumentStatusId || 0);
	const maxApprovalLevelBase = (Number(routingNumberOfApprovalLevels || 0) * 20) + 50;
	const approvalUpperBound = maxApprovalLevelBase + (10 * Number(isMultipleOf20(statusId)));

	if (statusId < 40) return Constants.VIEW;
	if (statusId === 40 || statusId === 60 || statusId === 180) return Constants.MAKE_CORRECTIONS;
	if (statusId === 170) return Constants.VIEW;

	if (
		statusId >= 50 &&
		statusId !== 60 &&
		statusId !== maxApprovalLevelBase &&
		statusId <= approvalUpperBound
	) {
		return Constants.APPROVAL;
	}

	return Constants.VIEW;
};


export const isApprovalLevelBreached = ({
	routingNumberOfApprovalLevels = 0,
	invoiceDocumentStatusId = 0
}) => {
	const noramlizedStatus = (invoiceDocumentStatusId) % 20 === 0 ? invoiceDocumentStatusId - 30 : invoiceDocumentStatusId;
	const maxStatus = (routingNumberOfApprovalLevels * 20) + 50;
	return maxStatus <= noramlizedStatus;
}

export const resolveWorkflowCapabilities = (
	invoiceDocument,
	{
		isAdmin = false,
		routingNumberOfApprovalLevels = 0,
		isDifferentCompany = false,
	} = {}
) => {
	const noramlizedStatus = (invoiceDocument?.invoiceDocumentStatusId) % 20 === 0 ? invoiceDocument?.invoiceDocumentStatusId - 30 : invoiceDocument?.invoiceDocumentStatusId;
	const statusId = Number(invoiceDocument?.invoiceDocumentStatusId || 0);
	const eligibleForOperation = !isDifferentCompany;

	const routingThreshold =
		(Number(routingNumberOfApprovalLevels || 0) * 20) +
		(50);

	const isAutoApproved = getIsAutoApproved(invoiceDocument);
	const hasPassedApproval = noramlizedStatus >= routingThreshold;
	const isNotPosted = statusId < 170;

	const overrideMode =
		isAdmin &&
		isAutoApproved &&
		hasPassedApproval &&
		isNotPosted &&
		eligibleForOperation;

	const canOverride =
		isAdmin &&
		isAutoApproved &&
		hasPassedApproval &&
		isNotPosted &&
		eligibleForOperation;

	const canEditDates =
		isAdmin &&
		hasPassedApproval &&
		isNotPosted &&
		!overrideMode &&
		eligibleForOperation;

	const canDelete = !hasPassedApproval &&
		statusId !== 170 &&
		statusId !== 180
	const canPostCreditMemo = invoiceDocument?.invoiceIndicator == 2 &&
		(statusId !== 170) &&
		hasPassedApproval;

	return {
		canPostToERP:
			hasPassedApproval &&
			statusId !== 170 &&
			statusId !== 180 &&
			eligibleForOperation,
		canRepostToERP: statusId === 180 && eligibleForOperation,
		canOverride,
		canEditDates,
		hasDifferentCompany: !eligibleForOperation,
		canDelete: canDelete,
		canPostCreditMemo: canPostCreditMemo
	};



};
// ---------------------------------------------
// Repost / Partial-post eligibility (UI-derived)
// ---------------------------------------------
// List already derives these values from row data. We mirror the same derivation here
// using the concrete facts available on invoiceDocument so editor and list behave the same.

export const isRepostRuleEditable = (userGroupRules = [], modelDefId) => {
	const md = Number(modelDefId || 0);
	return !!(userGroupRules || []).find(r =>
		r?.label === 'Repost Invoice' &&
		Number(r?.Model_def_id) === md &&
		String(r?.editable) === '1'
	);
};
export const isCreditMemoPostEnabled = (userGroupRules = [], modelDefId) => {
	const md = Number(modelDefId || 0);
	return !!(userGroupRules || []).find(r =>
		r?.label === 'Post Credit Memo' &&
		Number(r?.Model_def_id) === md &&
		String(r?.editable) === '1'
	);
};

export const derivePartialInvoiceStatus = (invoiceDocument, repostRuleEditable) => {
	const postStatus = (invoiceDocument?.invoicePostStatus || '').toString().toLowerCase();
	const isEligible = postStatus === 'partial posted' || postStatus === 'fully posted';

	const total = Number(invoiceDocument?.totalNumberInvoice || 0);
	const posted = Number(invoiceDocument?.postedNumberInvoice || 0);

	if (!isEligible || total <= 0) return null;

	// Fully posted -> view details (never a partial repost)
	if (posted >= total) {
		return { dataMode: 'ViewDetails', shouldDisplay: true, canRepost: false };
	}

	// Partially posted
	if (posted < total) {
		if (repostRuleEditable) {
			return { dataMode: 'PartialPost', shouldDisplay: true, canRepost: true };
		}
		// Rule not active -> still allow viewing details, but no repost
		return { dataMode: 'ViewDetails', shouldDisplay: true, canRepost: false };
	}

	return null;
};

export const getInvalidIoPoRowsForServiceTerm = (invoiceDocument) => {

	if (!invoiceDocument) return [];

	const {
		modelDefId,
		serviceDateFilterForIoLookup,
		serviceTermStartDateString,
		serviceTermEndDateString,
		invoiceDocumentEditorDetails
	} = invoiceDocument;

	if (modelDefId !== 1 || serviceDateFilterForIoLookup !== "MANDATORY")
		return [];

	if (!serviceTermStartDateString || !serviceTermEndDateString)
		return [];

	const invoiceStart = new Date(serviceTermStartDateString);
	const invoiceEnd = new Date(serviceTermEndDateString);

	invoiceStart.setHours(0, 0, 0, 0);
	invoiceEnd.setHours(0, 0, 0, 0);

	return (invoiceDocumentEditorDetails || []).filter(line => {

		if (!line.poCode || line.poCode.trim() === "" || !line.ioNumber || line.ioNumber.trim() === "") {
			return false;
		}

		if (!line.startDate || !line.endDate)
			return false;

		const ioStart = new Date(line.startDate);
		const ioEnd = new Date(line.endDate);

		ioStart.setHours(0, 0, 0, 0);
		ioEnd.setHours(0, 0, 0, 0);

		const hasOverlap =
			ioStart <= invoiceEnd &&
			ioEnd >= invoiceStart;

		return !hasOverlap;
	});
};